import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiCheckCircle,
  FiXCircle,
  FiRefreshCw,
  FiExternalLink,
  FiX,
  FiClock,
  FiLoader,
} from 'react-icons/fi';

import Breadcrumb from '../Components/Breadcrumb/Breadcrumb';
import PageHeader from '../Components/PageHeader/PageHeader';
import Button from '../Components/Button/Button';
import Banner from '../Components/Banner/Banner';
import PlatformBadge, { getPlatform } from '../Components/PlatformBadge/PlatformBadge';

import {
  getUploadJob,
  retryUploadTargets,
  cancelUploadJob,
} from '../Utilities/uploadApi';

import './uploadProgress.css';

const TERMINAL_JOB = new Set(['succeeded', 'partial', 'failed', 'cancelled']);
const TERMINAL_TARGET = new Set(['published', 'failed', 'cancelled']);

const JOB_LABEL = {
  queued: 'Queued',
  running: 'Publishing',
  succeeded: 'All published',
  partial: 'Finished with errors',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const TARGET_STATUS = {
  queued: { label: 'Queued', icon: <FiClock />, cls: 'ups-queued' },
  uploading: { label: 'Uploading', icon: <FiLoader className="ups-spin" />, cls: 'ups-running' },
  processing: {
    label: 'Processing',
    icon: <FiLoader className="ups-spin" />,
    cls: 'ups-running',
  },
  published: { label: 'Published', icon: <FiCheckCircle />, cls: 'ups-ok' },
  failed: { label: 'Failed', icon: <FiXCircle />, cls: 'ups-bad' },
  cancelled: { label: 'Cancelled', icon: <FiX />, cls: 'ups-muted' },
};

const ERROR_HINTS = {
  token_refresh_failed: {
    copy: 'Your platform login expired. Reconnect the account and retry.',
    actionLabel: 'Reconnect account',
  },
  missing_upload_scope: {
    copy: 'The upload permission was revoked. Re-enable it to retry.',
    actionLabel: 'Enable uploads',
  },
  platform_rejected_media: {
    copy: null, // show raw error_message
  },
  video_too_long: {
    copy: 'The video is longer than this platform allows.',
  },
  aspect_ratio_invalid: {
    copy: 'The aspect ratio is not accepted by this platform.',
  },
  platform_rate_limited: {
    copy: 'Retrying automatically…',
  },
  platform_unavailable: {
    copy: 'The platform is unreachable. Retrying automatically…',
  },
};

const POLL_BASE_MS = 2000;
const POLL_MAX_MS = 5000;
const MAX_NETWORK_RETRIES = 3;

function UploadProgress() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [connectionLost, setConnectionLost] = useState(false);
  const [retryBusy, setRetryBusy] = useState(false);

  const lastTargetsSig = useRef(null);
  const unchangedStreak = useRef(0);
  const failedFetches = useRef(0);
  const timer = useRef(null);

  const targetsSig = (j) =>
    JSON.stringify(j.targets.map((t) => [t.target_id, t.status, t.progress_pct]));

  const poll = useCallback(async () => {
    try {
      const fresh = await getUploadJob(jobId);
      failedFetches.current = 0;
      setConnectionLost(false);

      const sig = targetsSig(fresh);
      if (sig === lastTargetsSig.current) {
        unchangedStreak.current += 1;
      } else {
        unchangedStreak.current = 0;
        lastTargetsSig.current = sig;
      }
      setJob(fresh);

      if (TERMINAL_JOB.has(fresh.status)) return; // stop polling

      // Back off: 2s → 3s → 5s → 5s.
      const backoffTable = [POLL_BASE_MS, 3000, POLL_MAX_MS];
      const nextMs =
        backoffTable[Math.min(unchangedStreak.current, backoffTable.length - 1)] ||
        POLL_MAX_MS;
      timer.current = setTimeout(poll, nextMs);
    } catch (e) {
      failedFetches.current += 1;
      if (failedFetches.current >= MAX_NETWORK_RETRIES) {
        setConnectionLost(true);
        return;
      }
      timer.current = setTimeout(poll, POLL_BASE_MS);
    }
  }, [jobId]);

  useEffect(() => {
    poll();
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [poll]);

  const failedTargets = useMemo(
    () => (job?.targets || []).filter((t) => t.status === 'failed'),
    [job]
  );

  const handleRetryAll = async () => {
    if (!failedTargets.length) return;
    setRetryBusy(true);
    try {
      const fresh = await retryUploadTargets(
        jobId,
        failedTargets.map((t) => t.target_id)
      );
      setJob(fresh);
      lastTargetsSig.current = targetsSig(fresh);
      unchangedStreak.current = 0;
      if (timer.current) clearTimeout(timer.current);
      poll();
    } catch (e) {
      setError('Could not queue the retry.');
    } finally {
      setRetryBusy(false);
    }
  };

  const handleRetryOne = async (targetId) => {
    setRetryBusy(true);
    try {
      const fresh = await retryUploadTargets(jobId, [targetId]);
      setJob(fresh);
      unchangedStreak.current = 0;
      if (timer.current) clearTimeout(timer.current);
      poll();
    } finally {
      setRetryBusy(false);
    }
  };

  const handleCancel = async () => {
    try {
      const fresh = await cancelUploadJob(jobId);
      setJob(fresh);
    } catch (e) {
      setError('Could not cancel the job.');
    }
  };

  const handleManualRefresh = () => {
    failedFetches.current = 0;
    setConnectionLost(false);
    if (timer.current) clearTimeout(timer.current);
    poll();
  };

  if (!job) {
    return (
      <>
        <Breadcrumb items={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Upload' }]} />
        <div className="ups-empty">Loading job…</div>
      </>
    );
  }

  const jobIsTerminal = TERMINAL_JOB.has(job.status);

  return (
    <>
      <Breadcrumb
        items={[
          { label: 'Dashboard', to: '/dashboard' },
          { label: 'Account', to: `/accounts/${job.account_id}` },
          { label: 'Upload' },
        ]}
      />

      {error && (
        <Banner type="error" message={error} onDismiss={() => setError(null)} />
      )}
      {connectionLost && (
        <Banner
          type="error"
          message="Lost connection while checking job status."
          onDismiss={handleManualRefresh}
        />
      )}

      <PageHeader
        title="Upload progress"
        subtitle={JOB_LABEL[job.status] || job.status}
        actions={
          <>
            <Button
              variant="ghost"
              icon={<FiArrowLeft />}
              onClick={() => navigate(`/accounts/${job.account_id}`)}
            >
              Back to account
            </Button>
            {!jobIsTerminal && (
              <Button variant="ghost" icon={<FiX />} onClick={handleCancel}>
                Cancel queued
              </Button>
            )}
            {failedTargets.length > 0 && (
              <Button
                icon={<FiRefreshCw />}
                onClick={handleRetryAll}
                disabled={retryBusy}
              >
                Retry failed ({failedTargets.length})
              </Button>
            )}
            {connectionLost && (
              <Button variant="ghost" onClick={handleManualRefresh}>
                Refresh
              </Button>
            )}
          </>
        }
      />

      <div className="ups-target-list">
        {job.targets.map((t) => (
          <TargetRow
            key={t.target_id}
            target={t}
            onRetry={() => handleRetryOne(t.target_id)}
            retryBusy={retryBusy}
          />
        ))}
      </div>
    </>
  );
}

function TargetRow({ target: t, onRetry, retryBusy }) {
  const platformLabel = getPlatform(t.platform)?.label || t.platform;
  const status = TARGET_STATUS[t.status] || TARGET_STATUS.queued;
  const showBar = !TERMINAL_TARGET.has(t.status) && t.status !== 'queued';
  const hint = t.error_code ? ERROR_HINTS[t.error_code] : null;

  return (
    <div className={`ups-target ${status.cls}`}>
      <div className="ups-target-head">
        <div className="ups-target-left">
          <PlatformBadge platform={t.platform} size="md" />
          <div>
            <div className="ups-target-title">{platformLabel}</div>
            <div className="ups-target-sub">
              <span className={`ups-target-status ${status.cls}`}>
                {status.icon}
                {status.label}
              </span>
              {t.started_at && (
                <span className="ups-target-time">
                  started {new Date(t.started_at).toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="ups-target-right">
          {t.platform_content_url && (
            <a
              className="ups-target-link"
              href={t.platform_content_url}
              target="_blank"
              rel="noreferrer"
            >
              View post <FiExternalLink />
            </a>
          )}
          {t.status === 'failed' && (
            <Button size="sm" icon={<FiRefreshCw />} onClick={onRetry} disabled={retryBusy}>
              Retry
            </Button>
          )}
        </div>
      </div>

      {showBar && (
        <div className="ups-progress">
          <div
            className="ups-progress-bar"
            style={{ width: `${Math.max(5, t.progress_pct || 0)}%` }}
          />
        </div>
      )}

      {t.error_code && (
        <div className="ups-target-error">
          <strong>{t.error_code}:</strong>{' '}
          {hint?.copy || t.error_message || 'Something went wrong.'}
        </div>
      )}
    </div>
  );
}

export default UploadProgress;
